import { usePathname } from 'next/navigation'
import { useTranslation } from 'next-i18next'
import { useState, useEffect, useRef, useCallback } from 'react'
import { Swiper as SwiperInterface } from 'swiper'
import { SwiperRef } from 'swiper/react'

import { DateTime, STANDARD_DATE_OVERRIDES } from '@src/blocks/date-time'
import { Button, BUTTON_SIZE, BUTTON_TYPES, Image, Modal } from '@src/quince-components/src'
import ReviewStars from '@src/quince-components/src/atoms/reviews/reviewStars'
import Swiper from '@src/quince-components/src/atoms/swiper'
import { NAV_POSITION, DOTS_POSITION } from '@src/quince-components/src/atoms/swiper/Swiper'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import ProductReview from '@src/quince-site-common/src/domains/ProductReview'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import pdpStateManager from '@src/quince-site-common/src/redux/globalStateManager/PDPStateManager'
import plReviewPhotoPopupStateManager, {
  PLReviewPopupState,
} from '@src/quince-site-common/src/redux/globalStateManager/PLReviewPhotoPopupStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import { getSanitizedHtml } from '@src/quince-site-common/src/utils/getSanitizedHtml'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'

import styles from './review.module.scss'
import { getReviewImageAltText } from './utils'

interface SlideReview {
  imageUrl: string
  author: string
  rating: number
  createdAt: string
  title: string
  body: string
}

// PL-only desktop photo popup. Copy of ReviewPopUp with page-based pagination (driven by the PL
// gallery via plReviewPhotoPopupStateManager). The legacy popup is left untouched.
// Prefetch the next page once the user is within this many image slides of the last loaded one.
const DESKTOP_POPUP_PREFETCH_THRESHOLD = 3

const PLReviewPopupDesktop = () => {
  const pathname = usePathname()
  const { t } = useTranslation('common')
  const reviewReduxState = useAppSelector<PLReviewPopupState>(
    plReviewPhotoPopupStateManager,
    plReviewPhotoPopupStateManager.getReviewPopupState,
  )
  const productTitle = pdpStateManager.getProductTitleFromRedux()
  const [slideReviews, setSlideReviews] = useState<SlideReview[]>([])
  const carouselRef = useRef<SwiperRef | null>(null)

  const productReviews: ProductReview[] = reviewReduxState.productReviews
  const [initialPosition, setInitialPosition] = useState(-1)

  // Latch + live refs so the once-bound swiper callback always reads the current slide count / gate
  // (see handleAfterChange). Keeps each approach to the end firing a single fetch.
  const hasTriggeredNearEndRef = useRef(false)
  const slideCountRef = useRef(0)
  slideCountRef.current = slideReviews.length
  const hasMoreRef = useRef(false)
  hasMoreRef.current = reviewReduxState.hasMore

  // PL pagination: when the user navigates near the last loaded image, ask the gallery to load the
  // next page (it appends via reviewPopupStateManager.appendReviews). No-op for non-PL (hasMore is
  // false and no fetch handler is registered).
  const handleAfterChange = useCallback((swiper: SwiperInterface) => {
    const isNearEnd =
      slideCountRef.current > 0 && swiper.activeIndex >= slideCountRef.current - DESKTOP_POPUP_PREFETCH_THRESHOLD
    if (!isNearEnd) {
      hasTriggeredNearEndRef.current = false
      return
    }
    if (hasMoreRef.current && !hasTriggeredNearEndRef.current) {
      hasTriggeredNearEndRef.current = true
      plReviewPhotoPopupStateManager.triggerFetchMore()
    }
  }, [])

  useEffect(() => {
    const data: SlideReview[] = []
    if (reviewReduxState.showPopUp) {
      try {
        let count = 0
        productReviews.forEach(productReview => {
          const imageUrls = productReview.imagesFileUrls
          imageUrls?.forEach((imageUrl, i) => {
            const temp: SlideReview = {
              imageUrl,
              author: productReview.author,
              rating: productReview.rating,
              createdAt: productReview.createdAt,
              title: productReview.title,
              body: productReview.body,
            }
            if (productReview.id === reviewReduxState.reviewId) {
              if (i === reviewReduxState.imageIndex) {
                setInitialPosition(count)
              }
            }
            data.push(temp)
            count = count + 1
          })
        })
        setSlideReviews(data)
      } catch (err) {
        captureException(err, {
          module: MODULES.REVIEW,
          feature: FEATURES.REVIEW_IMAGE_POPUP,
          extraInfo: {
            originalError: JSON.stringify(err),
          },
        })
      }
    } else {
      setSlideReviews([])
    }
    // productReviews.length is included so PL appends (which grow the list) rebuild the slides;
    // initialPosition is only (re)set to the clicked image, so appends never move the user.
  }, [
    reviewReduxState.showPopUp,
    reviewReduxState.reviewId,
    reviewReduxState.imageIndex,
    reviewReduxState.productReviews.length,
  ])

  const handleClose = () => {
    plReviewPhotoPopupStateManager.hideReviewPopUp()
    setInitialPosition(-1)
  }
  const handleClick = () => {
    handleClose()
    setTimeout(() => {
      window.scroll({
        top: 0,
        left: 0,
        // @i18n-ignore
        behavior: 'smooth',
      })
    }, 0)
  }

  const deviceInfo = useDeviceInfo()
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE

  useEffect(() => {
    handleClose()
  }, [pathname])

  return (
    <Modal
      className={styles.modal}
      open={reviewReduxState.showPopUp && initialPosition !== -1}
      onClose={handleClose}
      showCloseButton={true}
    >
      <div className={styles.wrapper}>
        <Swiper
          id="review-popup-carousel"
          showDots={isMobile}
          showNavigator={!isMobile}
          numberOfVisibleSlides={1}
          variableWidth={isMobile}
          navPosition={NAV_POSITION.RIGHT_OVER_IMAGE}
          dotsPosition={DOTS_POSITION.BOTTOM}
          freeMode={false}
          ref={carouselRef}
          initialSlide={initialPosition}
          virtualOptions={{ enabled: true }}
          afterChange={handleAfterChange}
          prevIconClass={styles.prevIcon}
          isHorizontalCacheEnabled={false}
        >
          {slideReviews.map((productReview, imageIndex) => {
            return (
              // A review with 2+ photos yields multiple slides sharing author/createdAt, so include the
              // per-image index (and imageUrl) to keep sibling keys unique — otherwise virtualized slides
              // can reuse/misplace DOM. slideReviews is rebuilt wholesale on open (never reordered).
              <div key={`${productReview.author}_${productReview.createdAt}_${imageIndex}_${productReview.imageUrl}`}>
                <div className={styles.container}>
                  <div className={styles.left}>
                    <Image
                      fillWithBlackBackground={true}
                      title=""
                      className={styles.image}
                      url={productReview.imageUrl}
                      alt={
                        productTitle ? getReviewImageAltText({ reviewTitle: productReview.title, productTitle }) : ''
                      }
                    />
                  </div>
                  <div className={styles.right}>
                    <div className={styles.content}>
                      <div className={styles.ratingsAndUser}>
                        <ReviewStars
                          totalStars={5}
                          fillStars={productReview.rating}
                          className={styles.ratings}
                          classNameIcon={styles.ratingIcon}
                        />
                        <div className={styles.user}>
                          {productReview.author}{' '}
                          <DateTime
                            date={new Date(productReview.createdAt)}
                            formatName="NUMERIC_SLASH_DATE"
                            overrides={STANDARD_DATE_OVERRIDES.REVIEW_ITEM}
                          />
                        </div>
                      </div>
                      <div
                        className={styles.header}
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: getSanitizedHtml(productReview.title) }}
                      />
                      <div
                        className={styles.body}
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: getSanitizedHtml(productReview.body) }}
                      />
                      <Button
                        className={styles.shopNowButton}
                        type={BUTTON_TYPES.PRIMARY}
                        size={BUTTON_SIZE.LARGE}
                        disabled={false}
                        round={true}
                        onClick={handleClick}
                      >
                        {t('shop_now')}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </Swiper>
      </div>
    </Modal>
  )
}

export default PLReviewPopupDesktop
